import { themeColors } from '@/theme/colors';
import { Icon, Menu, Tag } from '@lego/connect-components-react';
import type { IconName } from '@lego/icons';
import React, { useRef, useState } from 'react';
import { MenuItem } from './menu-item';

type Option = {
  label: string;
  tag?: string;
  onClick?: (triggerElement: HTMLElement) => void;
  isOpen?: boolean;
};

type Segment = {
  title?: string;
  options: Array<Option>;
};

export const DropdownMenu = ({
  isActive,
  iconName,
  title,
  options,
  segments,
}: {
  isActive: boolean;
  iconName: IconName;
  title: string;
  options?: Array<Option>;
  segments?: Array<Segment>;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef<HTMLAnchorElement>(null);

  return (
    <>
      <MenuItem
        ref={anchorRef}
        isActive={isActive}
        isPressed={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        {title}
        <Icon
          name={iconName}
          size="small"
          isChild
          style={{ marginLeft: '8px' }}
        />
      </MenuItem>
      <Menu
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        anchorRef={anchorRef}
        aria-labelledby="menu"
        offset={0}
        style={{ width: 'max-content', minWidth: '220px', gap: '4px' }}
      >
        {segments
          ? segments.map((segment, index) => (
              <React.Fragment key={segment.title ?? index}>
                <Menu.Section
                  key={segment.title ?? index}
                  id={segment.title ?? index.toString()}
                  title={segment.title}
                >
                  {segment.options.map((option) => (
                    <Menu.Item
                      key={option.label}
                      value={option.label}
                      onClick={(event) => {
                        option.onClick?.(event.currentTarget);
                        setIsOpen(false);
                      }}
                      startEnhancer={<span style={{ width: '16px' }} />}
                      indicator={
                        option.tag ? (
                          <Tag size="small" variant="brand">
                            {option.tag}
                          </Tag>
                        ) : undefined
                      }
                      style={
                        option.isOpen
                          ? {
                              background:
                                themeColors.interactive.neutral.subtle.hovered,
                            }
                          : undefined
                      }
                    >
                      {option.label}
                    </Menu.Item>
                  ))}
                </Menu.Section>
              </React.Fragment>
            ))
          : options?.map((option) => (
              <Menu.Item
                key={option.label}
                value={option.label}
                onClick={(event) => {
                  option.onClick?.(event.currentTarget);
                  setIsOpen(false);
                }}
                style={
                  option.isOpen
                    ? {
                        background:
                          themeColors.interactive.neutral.subtle.hovered,
                      }
                    : undefined
                }
              >
                {option.label}
              </Menu.Item>
            ))}
      </Menu>
    </>
  );
};
